import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createIntakeContext } from "@/lib/intakeContext";
import type { IntakeContext } from "@/types/intake";

export type ContactMode = "closed" | "intake-form" | "intake-done";

type ContactContextValue = {
  open: boolean;
  mode: ContactMode;
  intakeContext: IntakeContext | null;
  submittedSummary: string | null;
  openIntakeContact: (ctx: IntakeContext) => void;
  completeIntake: (summary: string) => void;
  closeContact: () => void;
};

const ContactContext = createContext<ContactContextValue | null>(null);

export function ContactProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ContactMode>("closed");
  const [intakeContext, setIntakeContext] = useState<IntakeContext | null>(null);
  const [submittedSummary, setSubmittedSummary] = useState<string | null>(null);

  const openIntakeContact = useCallback((ctx: IntakeContext) => {
    setIntakeContext(ctx);
    setSubmittedSummary(null);
    setMode("intake-form");
  }, []);

  const completeIntake = useCallback((summary: string) => {
    setSubmittedSummary(summary);
    setMode("intake-done");
  }, []);

  const closeContact = useCallback(() => {
    setMode("closed");
    setIntakeContext(null);
    setSubmittedSummary(null);
  }, []);

  const open = mode !== "closed";

  const value = useMemo(
    () => ({
      open,
      mode,
      intakeContext,
      submittedSummary,
      openIntakeContact,
      completeIntake,
      closeContact,
    }),
    [
      open,
      mode,
      intakeContext,
      submittedSummary,
      openIntakeContact,
      completeIntake,
      closeContact,
    ],
  );

  return <ContactContext.Provider value={value}>{children}</ContactContext.Provider>;
}

export function useContact() {
  const ctx = useContext(ContactContext);
  if (!ctx) throw new Error("useContact must be used within ContactProvider");
  return ctx;
}

/** 全站联系入口：一律先留资（无攻略上下文时在表单内选校/身份）。 */
export function useOpenIntake() {
  const { openIntakeContact } = useContact();
  return useCallback(
    (partial: Parameters<typeof createIntakeContext>[0]) => {
      openIntakeContact(createIntakeContext(partial));
    },
    [openIntakeContact],
  );
}
